import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { 
  Handshake, 
  FileText, 
  Search, 
  Scale, 
  Wallet, 
  CheckCircle2, 
  Clock,
  Building2,
  ArrowRight,
  User,
  Calendar,
  AlertCircle
} from "lucide-react";

const DEAL_STAGES = [
  { key: "interest", label: "Interest", icon: Handshake, color: "bg-blue-500" },
  { key: "nda_signed", label: "NDA", icon: FileText, color: "bg-indigo-500" },
  { key: "due_diligence", label: "Due Diligence", icon: Search, color: "bg-purple-500" },
  { key: "loi_signed", label: "LOI", icon: Scale, color: "bg-pink-500" },
  { key: "escrow_funded", label: "Escrow", icon: Wallet, color: "bg-orange-500" },
  { key: "closing", label: "Closing", icon: Clock, color: "bg-amber-500" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "bg-emerald-500" }
];

const formatCurrency = (amount) => {
  if (!amount) return "TBD";
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const getStageIndex = (stage) => DEAL_STAGES.findIndex(s => s.key === stage);

const DealProgressBar = ({ currentStage }) => {
  const currentIndex = getStageIndex(currentStage);
  
  return (
    <div className="flex items-center gap-1 w-full">
      {DEAL_STAGES.map((stage, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        const Icon = stage.icon;
        
        return (
          <div key={stage.key} className="flex items-center flex-1">
            <div className={`
              flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium
              ${isCompleted ? 'bg-emerald-500 text-white' : isCurrent ? stage.color + ' text-white' : 'bg-slate-200 text-slate-500'}
            `}>
              {isCompleted ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <Icon className="w-4 h-4" />
              )}
            </div>
            {index < DEAL_STAGES.length - 1 && (
              <div className={`flex-1 h-1 mx-1 rounded ${isCompleted ? 'bg-emerald-500' : 'bg-slate-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const DealCard = ({ deal, userRole }) => {
  const currentStage = DEAL_STAGES.find(s => s.key === deal.current_stage);
  const isCompleted = deal.status === "completed";
  const isCancelled = deal.status === "cancelled";
  
  return (
    <Link to={`/deals/${deal.id}`}>
      <Card className={`hover:shadow-lg transition-all cursor-pointer border-2 ${
        isCompleted ? 'border-emerald-200 bg-emerald-50/30' : 
        isCancelled ? 'border-red-200 bg-red-50/30 opacity-75' : 
        'border-slate-100 hover:border-blue-200'
      }`} data-testid={`deal-card-${deal.id}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg mb-1">{deal.listing_name}</CardTitle>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {userRole === "buyer" ? `Seller: ${deal.seller_name}` : `Buyer: ${deal.buyer_name}`}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge className={`${
                isCompleted ? 'bg-emerald-600' : 
                isCancelled ? 'bg-red-600' : 
                currentStage?.color
              }`}>
                {isCancelled ? 'Cancelled' : currentStage?.label || deal.current_stage}
              </Badge>
              {deal.deal_value && (
                <span className="text-lg font-bold text-slate-900">{formatCurrency(deal.deal_value)}</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!isCancelled && (
            <div className="mb-4">
              <DealProgressBar currentStage={deal.current_stage} />
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(deal.created_at).toLocaleDateString()}
              </span>
              {deal.documents?.length > 0 && (
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {deal.documents.length} docs
                </span>
              )}
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default function Deals() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    fetchDeals();
  }, [user, navigate]);

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/deals");
      setDeals(data);
    } catch (error) {
      toast.error("Failed to fetch deals");
    } finally {
      setLoading(false);
    }
  };

  const filterDeals = (status) => {
    if (status === "all") return deals;
    if (status === "active") return deals.filter(d => d.status === "active");
    if (status === "completed") return deals.filter(d => d.status === "completed");
    if (status === "cancelled") return deals.filter(d => d.status === "cancelled");
    return deals;
  };

  const buyerDeals = deals.filter(d => d.buyer_id === user?.id);
  const sellerDeals = deals.filter(d => d.seller_id === user?.id);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8" data-testid="deals-header">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">My Deals</h1>
          <p className="text-slate-600">Track and manage your business transactions</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Deals</p>
                  <p className="text-3xl font-bold text-slate-900">{deals.length}</p>
                </div>
                <Handshake className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active</p>
                  <p className="text-3xl font-bold text-slate-900">{deals.filter(d => d.status === "active").length}</p>
                </div>
                <Clock className="w-10 h-10 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-3xl font-bold text-slate-900">{deals.filter(d => d.status === "completed").length}</p>
                </div>
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Value</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(deals.filter(d => d.status === "completed").reduce((sum, d) => sum + (d.deal_value || 0), 0))}
                  </p>
                </div>
                <Wallet className="w-10 h-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Deals Tabs */}
        <Tabs defaultValue="all" className="w-full" onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border">
            <TabsTrigger value="all" data-testid="tab-all">
              All Deals ({deals.length})
            </TabsTrigger>
            <TabsTrigger value="buying" data-testid="tab-buying">
              Buying ({buyerDeals.length})
            </TabsTrigger>
            <TabsTrigger value="selling" data-testid="tab-selling">
              Selling ({sellerDeals.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : deals.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Handshake className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Deals Yet</h3>
                  <p className="text-slate-600 mb-6">Start exploring businesses and show your interest to begin a deal</p>
                  <Link to="/marketplace">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      Browse Marketplace
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {deals.map(deal => (
                  <DealCard 
                    key={deal.id} 
                    deal={deal} 
                    userRole={deal.buyer_id === user.id ? "buyer" : "seller"} 
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="buying">
            {buyerDeals.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Buying Deals</h3>
                  <p className="text-slate-600 mb-6">Browse listings and show interest to start a buying deal</p>
                  <Link to="/marketplace">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      Find Businesses
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {buyerDeals.map(deal => (
                  <DealCard key={deal.id} deal={deal} userRole="buyer" />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="selling">
            {sellerDeals.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Selling Deals</h3>
                  <p className="text-slate-600 mb-6">List your business to attract buyers and start deals</p>
                  <Link to="/list-business">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      List Your Business
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {sellerDeals.map(deal => (
                  <DealCard key={deal.id} deal={deal} userRole="seller" />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
